import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollTo } from "@/lib/lenis";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config";
import { useSoundEffects } from "@/hooks/useSoundEffects";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isProjectPage = location.pathname.includes("/project/");
  const { hoverSound, clickSound, transitionSound } = useSoundEffects();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Animation entry delay for project pages
    const timer = setTimeout(
      () => {
        setIsVisible(true);
        if (isProjectPage) {
          // Play transition sound when navbar appears
          transitionSound();
        }
      },
      isProjectPage ? 300 : 0
    );

    // Page load animation
    const loadTimer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
      clearTimeout(loadTimer);
    };
  }, [isProjectPage, transitionSound]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();

    // Play click sound on navigation
    clickSound(e);

    // If it's an anchor link, handle smooth scroll
    if (href.startsWith("#")) {
      // If we're not on the home page, navigate to home first then scroll
      if (location.pathname !== "/") {
        navigate("/");
        // Wait for navigation then scroll
        setTimeout(() => {
          const elementId = href.substring(1); // Remove #
          const element = document.getElementById(elementId);
          if (element) {
            scrollTo(element, {
              duration: 1.5,
              easing: (t) => 1 - Math.pow(1 - t, 3),
              enableSound: false,
            });
          }
        }, 300); // Increased delay for page load
      } else {
        // We're on home page, scroll directly
        const elementId = href.substring(1); // Remove #
        const element = document.getElementById(elementId);
        if (element) {
          scrollTo(element, {
            duration: 1.5,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            enableSound: false,
          });
        }
      }
    } else if (href.startsWith("/#")) {
      // Handle home page anchor links from other pages
      const elementId = href.substring(2); // Remove /#
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            scrollTo(element, {
              duration: 1.5,
              easing: (t) => 1 - Math.pow(1 - t, 3),
              enableSound: false,
            });
          }
        }, 300); // Increased delay for page load
      } else {
        const element = document.getElementById(elementId);
        if (element) {
          scrollTo(element, {
            duration: 1.5,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            enableSound: false,
          });
        }
      }
    } else {
      // Use React Router for internal navigation
      navigate(href);
    }
  };

  // Handle logo click
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    clickSound(e);
  };

  // Handle logo hover
  const handleLogoHover = (e: React.MouseEvent<HTMLAnchorElement>) => {
    hoverSound(e);
  };

  // Handle hire me click with sound - using the same approach as navigation links
  const handleHireMeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    clickSound(e);

    // Check if we need to navigate to home first
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const contactElement = document.getElementById("contact");
        if (contactElement) {
          scrollTo(contactElement, {
            duration: 1.8,
            easing: (t) => 1 - Math.pow(1 - t, 4),
            enableSound: false,
          });
        }
      }, 300);
    } else {
      // Use Lenis for smooth scroll to contact section
      const contactElement = document.getElementById("contact");
      if (contactElement) {
        scrollTo(contactElement, {
          duration: 1.8,
          easing: (t) => 1 - Math.pow(1 - t, 4), // Ease out quart for hire me button
          enableSound: false, // We already played the click sound
        });
      }
    }
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 w-full z-50 transition-all duration-500",
        isScrolled ? "py-2 bg-black border-b border-white/10" : "py-4",
        isProjectPage && "transform transition-transform duration-700",
        isProjectPage && (isVisible ? "translate-y-0" : "-translate-y-full")
      )}
    >
      <div className="container mx-auto flex justify-between items-center px-4">
        <Link
          to="/"
          className="font-display text-2xl tracking-wider cursor-pointer transform transition-all duration-500"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
          }}
          onMouseEnter={handleLogoHover}
          onClick={handleLogoClick}
        >
          {siteConfig.name.split(" ")[0][0]}
          {siteConfig.name.split(" ")[1][0]}
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8 items-center">
          {siteConfig.navigation.map((link, index) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              onMouseEnter={hoverSound}
              className="text-sm uppercase tracking-widest hover:opacity-60 transition-all duration-300 cursor-pointer"
              style={{
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                transition: "opacity 0.5s ease, transform 0.5s ease",
                transitionDelay: `${200 + index * 100}ms`,
              }}
            >
              {link.name}
            </a>
          ))}

          {/* Hire Me Button for Desktop */}
          <a
            href="#contact"
            onClick={handleHireMeClick}
            onMouseEnter={hoverSound}
            className="text-sm uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-all duration-300 group relative overflow-hidden"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
              transitionDelay: `${200 + siteConfig.navigation.length * 100}ms`,
            }}
          >
            <span className="relative z-10 transition-colors duration-300">
              Hire Me
            </span>
            <span className="absolute inset-0 bg-white transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300"></span>
          </a>
        </nav>

        {/* Mobile Hire Me Button - Only visible on mobile */}
        <a
          href="#contact"
          onClick={handleHireMeClick}
          onMouseEnter={hoverSound}
          className="md:hidden text-sm uppercase tracking-widest border border-white px-3 py-1.5 hover:bg-white hover:text-black transition-all duration-300 relative overflow-hidden group"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
            transitionDelay: "400ms",
          }}
        >
          <span className="relative z-10 transition-colors duration-300">
            Hire Me
          </span>
          <span className="absolute inset-0 bg-white transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300"></span>
        </a>
      </div>
    </header>
  );
};

export default Navbar;
